import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { TShirtIcon } from '@phosphor-icons/react'

import FiltersSection from '@/common/components/FiltersSection/FiltersSection'
import MetricsBreadcrumbs from '@/metrics/components/MetricsBreadcrumbs/MetricsBreadcrumbs'
import ProductsCard from '@/metrics/components/ProductsCard/ProductsCard'
import { groupByValues } from '@/metrics/constants'
import { entityTypes } from '@/metrics/hooks/useMetrics/constants'
import useMetrics from '@/metrics/hooks/useMetrics/useMetrics'
import { getIsReasonSummaryPending } from '@/metrics/pages/ReasonMetricDetailPage/helpers'
import useReasonDetailSetup from '@/metrics/pages/ReasonMetricDetailPage/hooks/useReasonDetailSetup/useReasonDetailSetup'
import useReasonSummary from '@/metrics/pages/ReasonMetricDetailPage/hooks/useReasonSummary/useReasonSummary'
import { productsDescriptions } from '@/metrics/pages/ReasonMetricDetailPage/ReasonMetrics/constants'
import ReasonSummaryHeader from '@/metrics/pages/ReasonMetricDetailPage/ReasonSummaryHeader/ReasonSummaryHeader'
import metricsServices from '@/metrics/services/services'
import routes from '@/Router/routes.constants'

import * as Styles from './styles'

const SubReasonMetricsDetailPage = ({ isUnifiedDashboard }) => {
  const { reasonKey, subReasonId } = useParams()
  const { t } = useTranslation()

  const {
    regions,
    commonFilters,
    setCommonFilters,
    getUrlSearchParams,
    productsFilters,
    setProductsFilters,
    returnPortalId,
    salesOrdersDataActivated,
    showExchanges,
    subReasonsOrdering,
    reasonSummary,
    loadingReasonSummary,
    reasonSummaryError,
  } = useReasonDetailSetup({ reasonKey, isUnifiedDashboard })

  const showSales = salesOrdersDataActivated || isUnifiedDashboard

  const {
    reasonSummary: subReasonSummary,
    loadingReasonSummary: loadingSubReasonSummary,
    reasonSummaryError: subReasonSummaryError,
  } = useReasonSummary({
    reasonKey,
    subReasonId,
    filters: commonFilters,
    ordering: subReasonsOrdering,
    returnPortalId,
  })

  const { data: subReasonTopReturnedProducts, loading: loadingSubReasonTopReturnedProducts } = useMetrics({
    returnPortalId,
    service: metricsServices.getReasonTopReturnedProducts,
    entityId: reasonKey,
    entityType: entityTypes.reason,
    filters: commonFilters,
    additionalFilters: {
      subReasonId,
      ordering: productsFilters?.ordering,
    },
  })

  const getDashboardUrl = () => {
    return `${isUnifiedDashboard ? routes.merchantDashboardPage : routes.dashboardPage}?${getUrlSearchParams()}`
  }

  const getReasonUrl = () => {
    const reasonPath = isUnifiedDashboard
      ? routes.merchantDashboardReasonMetricsDetailPage
      : routes.reasonMetricsDetailPage

    return `${reasonPath.replace(':reasonKey', reasonKey)}?${getUrlSearchParams()}`
  }

  const getBreadcrumbs = () => {
    return [
      { title: t('Dashboard'), url: getDashboardUrl() },
      { title: t('Motivos') },
      {
        title: reasonSummary?.display || t('Motivo'),
        url: getReasonUrl(),
        loading: getIsReasonSummaryPending({
          summary: reasonSummary,
          loading: loadingReasonSummary,
          error: reasonSummaryError,
        }),
      },
      {
        title: subReasonSummary?.display || t('Submotivo'),
        loading: getIsReasonSummaryPending({
          summary: subReasonSummary,
          loading: loadingSubReasonSummary,
          error: subReasonSummaryError,
        }),
      },
    ]
  }

  const getProductsTooltip = () => {
    if (!showSales) return null

    return t('Las unidades vendidas son las totales del producto en el periodo, no solo las devueltas por este motivo.')
  }

  const productsDescription = showSales ? productsDescriptions.withSales : productsDescriptions.withoutSales

  return (
    <Styles.Container>
      <Styles.HeaderContainer>
        <MetricsBreadcrumbs breadcrumbs={getBreadcrumbs()} />
        <FiltersSection regions={regions} filters={commonFilters} onChange={setCommonFilters} />
      </Styles.HeaderContainer>
      <ReasonSummaryHeader
        summary={subReasonSummary}
        loading={loadingSubReasonSummary}
        error={subReasonSummaryError}
        showExchanges={showExchanges}
        isSubReason
      />
      <ProductsCard
        Icon={TShirtIcon}
        title={t('Productos más devueltos')}
        description={t(productsDescription)}
        tooltip={getProductsTooltip()}
        rateLabel={t('% Tasa de devolución de productos')}
        showSearch={false}
        loading={loadingSubReasonTopReturnedProducts}
        products={subReasonTopReturnedProducts?.results}
        showGroupBySelector={false}
        showExport={false}
        productsFilters={{ groupBy: groupByValues.products, ordering: productsFilters?.ordering }}
        onChangeProductsFilters={setProductsFilters}
        salesOrdersDataActivated={showSales}
        isUnifiedDashboard={isUnifiedDashboard}
      />
    </Styles.Container>
  )
}

export default SubReasonMetricsDetailPage
